import React, {useState, useEffect} from "react";

import {BrowserRouter as Router, Routes, Route} from "react-router";
import {Security, LoginCallback, useOktaAuth} from '@okta/okta-react';
import {OktaAuth, toRelativeUrl} from "@okta/okta-auth-js";
import {config} from './components/Session/authConfig';
import uuidv4 from 'uuid/v4';

import DashboardPageContainer2 from './containers/DashboardPageContainer';
import NewDashboardPageContainer from './containers/NewDashboardPageContainer';
import ContactUsPageContainer from './containers/ContactUsPageContainer';
import LegalPageContainer from './containers/LegalPageContainer';
import LogoutPageContainer from './containers/LogoutPageContainer';
import LinkedAccountFailLockedOutContainer from './containers/LinkedAccountFailLockedOutContainer';
import MyAccountPageContainer from './containers/MyAccountPageContainer';
import MyDocumentsPageContainer from './containers/MyDocumentsPageContainer';
import MyProfilePageContainer from './containers/MyProfilePageContainer';
import NewAccountPageContainer2 from './containers/NewAccountPageContainer';
import withFeatureFlags from './components/FeatureFlags/withFeatureFlags';
import LoginPageContainer from "./containers/LoginPageContainer";
import ScrollToTop from './utils/ScrollToTop';
import TimeoutPageContainer from "./containers/TimeoutPageContainer";
import FeatureFlags from './components/FeatureFlags/FeatureFlags';
import LiveChat from './components/LiveChat/LiveChat';
import ForgotEmailPageContainer from "./containers/ForgotEmailPageContainer";
import ForgotPasswordPageContainer from "./containers/ForgotPasswordPageContainer";
import ResetPasswordPageContainer from "./containers/ResetPasswordPageContainer";
import ApolloContextProvider from './contexts/ApolloContextProvider';
import CurrentUserProvider from "./contexts/CurrentUserProvider";
import SessionId from "./contexts/SessionId";
import MaturityInstructionPageContainer2 from "./containers/MaturityInstructionPageContainer";

import Logger, {logMessage, getDurableSessionId, useLogger} from "./contexts/Logger";
import {parse} from "./utils/jwt";
import MetaTags from "./components/MetaTags/MetaTags";
import MyAccountPageContainer1 from "./containers/MyAccountPageContainer1";
import AnalyticsWrapper from "./contexts/AnalyticsWrapper";
import TroubleLoggingIn from "./components/Session/TroubleLoggingIn";
import TroubleLoggingInPageContainer from "./containers/TroubleLoggingInPageContainer";
import CloseFisaAccountPageContainer from "./containers/CloseFisaAccountPageContainer";
import DisaMaturityPageContainer from "./containers/DisaMaturityPageContainer";
import IsaTransferPageContainer from "./containers/IsaTransferPageContainer";

import { setIdsAssetPaths } from '@investec/ids-web-react';
import iconsCorePath from '@investec/ids-web-theme-investec/assets/icons-core.svg';
import iconsOtherPath from '@investec/ids-web-theme-investec/assets/icons-other.svg';

const baseOktaConfig = {
    issuer: config.issuer,
    clientId: config.clientId,
    redirectUri: window.location.origin + '/implicit/callback',
    tokenManager: {
        storage: 'sessionStorage'
    }
};

setIdsAssetPaths({
	'icons-core.svg': iconsCorePath,
	'icons-other.svg': iconsOtherPath,
});

// Enhanced configuration for silent token renewal
const silentRenewalOktaConfig = {
    issuer: config.issuer,
    clientId: config.clientId,
    redirectUri: window.location.origin + '/implicit/callback',
    tokenManager: {
        storage: 'sessionStorage',
        autoRenew: true, // Enable automatic token renewal
        autoRemove: false, // Don't auto-remove tokens to prevent auth loops
        //expireEarlySeconds: 30, // Start renewal 30 seconds before expiry
        syncStorage: true // Sync tokens across tabs
    },
    scopes: ['openid', 'profile', 'email', 'offline_access'], // Include offline_access for refresh tokens
    pkce: true, // Enable PKCE for better security
    responseType: /** @type {['token', 'id_token']} */ (['token', 'id_token']), // Use implicit flow
    devMode: false // Disable dev mode
};

let oktaAuth = new OktaAuth(baseOktaConfig);

const customAuthHandler = (/** @type {unknown} */ _props) => {
    window.location.href = '/login';
};

const restoreOriginalUri = (/** @type {OktaAuth} */ _oktaAuth, /** @type {string} */ originalUri) => {
    window.location.href = toRelativeUrl(originalUri || '/', window.location.origin);
    return Promise.resolve();
};

const ProtectedRoute = (/** @type {{ component: React.ComponentType<any> }} */ {component: Component}) => {
    const {authState} = useOktaAuth();

    useEffect(() => {
        if (authState && !authState.isAuthenticated) {
            customAuthHandler({});
        }
    }, [authState]);

    if (!authState || !authState.isAuthenticated) {
        return null;
    }

    return <Component />;
};

const getCustomerId = (/** @type { string } */ token) => {
    if (!token) return '';
    return parse(token).investecId;
};

const setAuthSessionState = (/** @type {boolean | undefined} */ state) => {
    sessionStorage.setItem('auth-state', state ? 'true' : 'false');
}

const getAuthSessionState = () => {
    return sessionStorage.getItem('auth-state') === 'true';
}

const DashboardRouting = withFeatureFlags((props) => {
    const logger = useLogger();

    useEffect(() => {
        logger.info(`Navigated to: ${window.location.href}`);
    }, []);

    // Show new dashboard when feature flag is enabled, otherwise show old dashboard
    const DashboardComponent = props.featureEnabled('EnableNewDashboardPage')
        ? NewDashboardPageContainer 
        : DashboardPageContainer2;
    const resetPasswordRouteEnabled = props.featureEnabled('DashboardPasswordPolicyV2Enabled');

    return(
        <>
            <LiveChat/>
            <Routes>
                <Route path='/' element={<ProtectedRoute component={DashboardComponent}/>}/>
                <Route path='/contact-us' element={<ContactUsPageContainer/>}/>
                <Route path='/legal' element={<ProtectedRoute component={LegalPageContainer}/>}/>
                <Route path='/logout' element={<LogoutPageContainer/>}/>
                <Route path='/login' element={<LoginPageContainer/>}/>
                <Route path='/timeout' element={<TimeoutPageContainer/>}/>
                <Route path='/implicit/callback/*' element={<LoginCallback/>}/>
                <Route path='/forgot-password/*' element={<ForgotPasswordPageContainer/>}/>
                {resetPasswordRouteEnabled && <Route path='/reset-password/*' element={<ResetPasswordPageContainer/>}/>}
                <Route path="/forgot-email/*" element={<ForgotEmailPageContainer/>}/>
                <Route path="/login-help/*" element={<TroubleLoggingInPageContainer/>}/>
                <Route path="/maturity/disa" element={<ProtectedRoute component={DisaMaturityPageContainer}/>}/>
                <Route path="/maturity-instruction/*" element={<ProtectedRoute component={MaturityInstructionPageContainer2}/>}/>
                <Route path="/isa-transfer/:accountId" element={<ProtectedRoute component={IsaTransferPageContainer}/>}/>
                <Route path="/my-profile" element={<ProtectedRoute component={MyProfilePageContainer}/>}/>
                <Route path="/my-documents/*" element={<ProtectedRoute component={MyDocumentsPageContainer}/>}/>
                <Route path="/account-details/*" element={<ProtectedRoute component={MyAccountPageContainer}/>}/>
                <Route path="/new-account/*" element={<ProtectedRoute component={NewAccountPageContainer2}/>}/>
                <Route path="/linked-account/locked/*" element={<LinkedAccountFailLockedOutContainer/>}/>
                <Route path="/my-linked-account/*" element={<ProtectedRoute component={MyAccountPageContainer1}/>} />
                <Route path="/close-account/*" element={<ProtectedRoute component={CloseFisaAccountPageContainer}/>}/>
            </Routes>
        </>
    )
})

const App = withFeatureFlags(() => {

    let [sessionId] = useState(uuidv4());
    let [authToken, setAuthToken] = useState(/** @type {string | null} */ (null));
    let lastAuthTokenRef = React.useRef(/** @type {string | null} */ (null));    // N.B. this block of logging is the result of a lot of
    // debugging to correctly detect the change in state of the
    // okta session, please don't change it unless you know what
    // you're doing and you're testing the effect on the
    // relevant ops-log output.
    useEffect(() => {
        oktaAuth.authStateManager.subscribe(function (authState) {

            let lastState = getAuthSessionState();
            if (authState.isAuthenticated && authState.accessToken && authState.accessToken.accessToken && !lastState) {
                const newToken = authState.accessToken.accessToken;
                if (lastAuthTokenRef.current !== newToken) {
                    setAuthToken(newToken);
                    lastAuthTokenRef.current = newToken;
                }
                
                let cid = getCustomerId(newToken);
                
            } else if (authState.isAuthenticated && authState.accessToken && authState.accessToken.accessToken && lastState ) {
                // Token was renewed - update the token state silently
                const newToken = authState.accessToken.accessToken;
                if (lastAuthTokenRef.current !== newToken) {
                    setAuthToken(newToken);
                    lastAuthTokenRef.current = newToken;
                }
            } else if (!authState.isAuthenticated && lastState) {
                // Added under the guidance of okta engineers to ensure another user logging after a previous
                // users session was expired doesn't renew the old one.
                oktaAuth.tokenManager.clear();

                setAuthToken(null);
                lastAuthTokenRef.current = null;
            }

            setAuthSessionState(authState.isAuthenticated);
        });        
        // Handle token renewal events to prevent page refreshes
            oktaAuth.tokenManager.on('renewed', function (key, newToken, _oldToken) {
                if (key === 'accessToken' && newToken && 'accessToken' in newToken) {
                const tokenValue = newToken.accessToken;
                if (lastAuthTokenRef.current !== tokenValue) {
                    setAuthToken(tokenValue);
                    lastAuthTokenRef.current = tokenValue;
                }
                // Don't trigger authState update for renewals - let the token manager handle it
            }
        });

        oktaAuth.tokenManager.on('error', function (err) {
            console.error('Token manager error:', err);
            // Don't trigger page redirects on token errors
            // This prevents page refreshes during failed renewals
        });

            oktaAuth.tokenManager.on('expired', function (key, expiredToken) {
            // Let auto-renewal handle expired tokens
        });

        oktaAuth.start().catch(err => { console.error('Failed: oktaAuth.start()', err); });        
        return () => {
            oktaAuth.authStateManager.unsubscribe();
            oktaAuth.tokenManager.off('renewed');
            oktaAuth.tokenManager.off('error');
            oktaAuth.tokenManager.off('expired');
            oktaAuth.stop().catch(err => { console.error('Failed: oktaAuth.stop()', err); });
        }
    }, []);

    return (
        <>
            <MetaTags/>
            <Router>
                <ScrollToTop>
                    <SessionId sessionId={sessionId}>
                        <Security
                            oktaAuth={oktaAuth}
                            onAuthRequired={customAuthHandler}
                            restoreOriginalUri={restoreOriginalUri}>
                            <Logger sessionId={sessionId} authToken={authToken}>
                                <CurrentUserProvider>
                                    <AnalyticsWrapper>
                                        <ApolloContextProvider>
                                            <FeatureFlags>
                                                <DashboardRouting />
                                            </FeatureFlags>
                                        </ApolloContextProvider>
                                    </AnalyticsWrapper>
                                </CurrentUserProvider>
                            </Logger>
                        </Security>
                    </SessionId>
                </ScrollToTop>
            </Router>
        </>
    );
});

export default withFeatureFlags(App);
